import type {
  CreateTranscriptMessageArgs,
  VoiceTranscriptPersistenceResult,
} from "@/types/voice-chat"
import type {
  TranscriptAuditPayload,
  TranscriptPersistOptions,
  VoiceFinalizedTranscriptTurn,
  VoiceTurnPersistFailure,
} from "@/types/voice-chat-rtc"

import { errorMessage } from "@/lib/exception-utils"
import { wait, withTimeout } from "@/lib/promise-utils"
import {
  currentTraceEventSeq,
  voiceTracer,
} from "@/lib/voice-trace/voice-trace"

const TRANSCRIPT_PERSIST_ATTEMPT_TIMEOUT_MS = 15_000
const TRANSCRIPT_PERSIST_TIMEOUT_MESSAGE = "Transcript persist timed out."
const TRANSCRIPT_PERSIST_MAX_AUTO_ATTEMPTS = 5

const persistTrace = voiceTracer("persist")

interface PendingTranscriptPersist {
  chatId: number
  turn: VoiceFinalizedTranscriptTurn
  openaiCallId: string
  auditPayload: TranscriptAuditPayload
  eventSeq?: number | null
  inFlight: boolean
  deferred: TranscriptPersistenceDeferred
}

interface TranscriptPersistenceDeferred {
  promise: Promise<VoiceTranscriptPersistenceResult>
  resolve: (result: VoiceTranscriptPersistenceResult) => void
  reject: (error: unknown) => void
  enqueued: boolean
}

interface TranscriptPersistQueueDeps {
  persistTranscript(
    args: CreateTranscriptMessageArgs
  ): Promise<VoiceTranscriptPersistenceResult>
  onTurnPersisting(payload: { providerEventId: string }): void
  onTurnPersisted(
    turn: VoiceFinalizedTranscriptTurn,
    result: VoiceTranscriptPersistenceResult
  ): void
  onTurnPersistFailed(payload: VoiceTurnPersistFailure): void
}

/**
 * Provides FIFO transcript durability. Provider item identity supplies one
 * deferred/replacement target even when a consumer starts waiting before the turn
 * is enqueued. Queued corrections may replace one another, but immutable in-flight
 * requests cannot; an exhausted head failure rejects its tail. `settled` exposes
 * the current FIFO worker's completion promise for teardown after its final flush.
 */
export class TranscriptPersistQueue {
  private readonly pendingTranscriptPersists: PendingTranscriptPersist[] = []
  private persistWorkerRunning = false
  private persistWorkerSettled: Promise<void> = Promise.resolve()
  private readonly deferreds = new Map<string, TranscriptPersistenceDeferred>()

  constructor(private deps: TranscriptPersistQueueDeps) {}

  get settled() {
    return this.persistWorkerSettled
  }

  // This is a single FIFO worker because transcript order is the chat's durable
  // order. Snapshot mutable turn/audit inputs at enqueue time: later realtime
  // truncation or stream updates must not rewrite a request already queued.
  enqueue(
    chatId: number,
    turn: VoiceFinalizedTranscriptTurn,
    openaiCallId: string,
    auditPayload: TranscriptAuditPayload = {},
    options: TranscriptPersistOptions = {}
  ) {
    const queuedTurn: VoiceFinalizedTranscriptTurn = {
      ...turn,
      providerEventIds: turn.providerEventIds
        ? [...turn.providerEventIds]
        : undefined,
      toolCallIds: turn.toolCallIds ? [...turn.toolCallIds] : undefined,
    }
    const queuedAuditPayload = { ...auditPayload }
    const existingPersistence = this.deferreds.get(turn.providerEventId)
    // A correction may replace a queued turn, but never an in-flight request:
    // replacing the latter would make its eventual persistence callback describe
    // different content than the request sent to the backend.
    const queuedReplacementTarget = this.pendingTranscriptPersists.find(
      (candidate) =>
        candidate.turn.providerEventId === turn.providerEventId &&
        !candidate.inFlight
    )
    if (existingPersistence?.enqueued && options.replaceExisting !== true) {
      return existingPersistence.promise
    }

    const eventSeq = currentTraceEventSeq()
    const itemTrace = voiceTracer(
      "persist",
      {
        providerEventId: turn.providerEventId,
        role: turn.role,
      },
      eventSeq
    )
    itemTrace.info("transcript_persist_enqueued", {
      interrupted: turn.interrupted === true,
      transcriptLength: turn.transcript.length,
    })

    const deferred = this.deferredFor(turn.providerEventId)
    deferred.enqueued = true
    if (queuedReplacementTarget) {
      queuedReplacementTarget.chatId = chatId
      queuedReplacementTarget.turn = queuedTurn
      queuedReplacementTarget.openaiCallId = openaiCallId
      queuedReplacementTarget.auditPayload = queuedAuditPayload
      queuedReplacementTarget.eventSeq = eventSeq
    } else {
      this.pendingTranscriptPersists.push({
        chatId,
        turn: queuedTurn,
        openaiCallId,
        auditPayload: queuedAuditPayload,
        eventSeq,
        inFlight: false,
        deferred,
      })
    }
    this.startTranscriptPersistWorker()
    return deferred.promise
  }

  // Persistence dependents wait on provider item identity, not stream position.
  // They can register before the turn is enqueued; the deferred bridges that race
  // and is explicitly rejected when the session can no longer supply the turn.
  persistenceFor(itemId: string) {
    persistTrace.info("voice_tool_user_item_wait_started", { itemId })
    const promise = this.deferredFor(itemId).promise
    void promise
      .then(() => {
        persistTrace.info("voice_tool_user_item_wait_resolved", { itemId })
      })
      .catch((error: unknown) => {
        persistTrace.warn("voice_tool_user_item_wait_failed", {
          itemId,
          message: errorMessage(error, "Could not save transcript."),
        })
      })
    return promise
  }

  rejectOutstanding(reason: string) {
    Array.from(this.deferreds).forEach(([itemId, deferred]) => {
      if (deferred.enqueued) return

      persistTrace.warn("voice_tool_user_item_wait_rejected", {
        itemId,
        reason,
      })
      deferred.reject(new Error(`User transcript unavailable: ${reason}.`))
      this.deferreds.delete(itemId)
    })
  }

  private createDeferred(itemId: string) {
    let resolve: TranscriptPersistenceDeferred["resolve"] | null = null
    let reject: TranscriptPersistenceDeferred["reject"] | null = null
    const promise = new Promise<VoiceTranscriptPersistenceResult>(
      (resolvePromise, rejectPromise) => {
        resolve = resolvePromise
        reject = rejectPromise
      }
    )
    if (!resolve || !reject) {
      throw new Error("Transcript persistence deferred was not initialized.")
    }

    const deferred: TranscriptPersistenceDeferred = {
      promise,
      resolve,
      reject,
      enqueued: false,
    }
    this.deferreds.set(itemId, deferred)
    void promise.catch(() => undefined)
    return deferred
  }

  private deferredFor(itemId: string) {
    return this.deferreds.get(itemId) ?? this.createDeferred(itemId)
  }

  private async attemptTranscriptPersist(
    pending: PendingTranscriptPersist
  ): Promise<VoiceTranscriptPersistenceResult> {
    const { chatId, turn, openaiCallId, auditPayload } = pending
    const itemTrace = voiceTracer(
      "persist",
      { providerEventId: turn.providerEventId, role: turn.role },
      pending.eventSeq
    )
    const result = await withTimeout(
      this.deps.persistTranscript({
        chatId,
        role: turn.role,
        transcript: turn.transcript,
        providerEventId: turn.providerEventId,
        providerEventIds: turn.providerEventIds,
        toolCallIds: turn.toolCallIds,
        openaiCallId,
        interrupted: turn.interrupted,
        fullTranscript: auditPayload.fullTranscript,
        truncatedAtMs: auditPayload.truncatedAtMs,
        cutoffReason: auditPayload.cutoffReason,
      }),
      TRANSCRIPT_PERSIST_ATTEMPT_TIMEOUT_MS,
      TRANSCRIPT_PERSIST_TIMEOUT_MESSAGE
    )

    this.deps.onTurnPersisted(turn, result)
    itemTrace.info("transcript_persist_succeeded", {
      interrupted: turn.interrupted === true,
      transcriptLength: turn.transcript.length,
      messageId: result.messageId,
    })
    return result
  }

  private async runTranscriptPersistWorker() {
    if (this.persistWorkerRunning) return
    this.persistWorkerRunning = true

    try {
      while (this.pendingTranscriptPersists.length > 0) {
        const pending = this.pendingTranscriptPersists[0]
        const { turn } = pending
        pending.inFlight = true
        const itemTrace = voiceTracer(
          "persist",
          { providerEventId: turn.providerEventId, role: turn.role },
          pending.eventSeq
        )
        this.deps.onTurnPersisting({ providerEventId: turn.providerEventId })

        let succeeded = false
        for (
          let attempt = 1;
          attempt <= TRANSCRIPT_PERSIST_MAX_AUTO_ATTEMPTS;
          attempt += 1
        ) {
          itemTrace.info("transcript_persist_queue_started", {
            interrupted: turn.interrupted === true,
            transcriptLength: turn.transcript.length,
            attempt,
          })
          try {
            const result = await this.attemptTranscriptPersist(pending)
            this.pendingTranscriptPersists.shift()
            pending.deferred.resolve(result)
            succeeded = true
            break
          } catch (error) {
            itemTrace.warn("transcript_persist_attempt_failed", {
              interrupted: turn.interrupted === true,
              transcriptLength: turn.transcript.length,
              attempt,
              message: errorMessage(error, "Could not save transcript."),
            })
            if (attempt < TRANSCRIPT_PERSIST_MAX_AUTO_ATTEMPTS) {
              const backoff = Math.min(2 ** (attempt - 1) * 500, 8_000)
              const jitter = Math.floor(Math.random() * 250)
              await wait(backoff + jitter)
            } else {
              const message = errorMessage(error, "Could not save transcript.")
              const failureContext = {
                rootProviderEventId: turn.providerEventId,
                attemptCount: TRANSCRIPT_PERSIST_MAX_AUTO_ATTEMPTS,
                affectedTurnCount: this.pendingTranscriptPersists.length,
                openaiCallId: pending.openaiCallId,
              }
              itemTrace.warn("transcript_persist_failed", {
                interrupted: turn.interrupted === true,
                transcriptLength: turn.transcript.length,
                message,
              })
              this.deps.onTurnPersistFailed({
                providerEventId: turn.providerEventId,
                message,
                ...failureContext,
              })
              this.pendingTranscriptPersists.shift()
              if (
                this.pendingTranscriptPersists.some(
                  (queued) =>
                    queued.turn.providerEventId === turn.providerEventId &&
                    !queued.inFlight
                )
              ) {
                succeeded = true
                break
              }
              pending.deferred.reject(error)
              this.rejectQueuedTranscriptPersists(error, failureContext)
            }
          }
        }

        if (!succeeded) break
      }
    } finally {
      this.persistWorkerRunning = false
    }
  }

  // Once the head exhausts retries, reject the tail instead of issuing later
  // writes out of context. A later turn must not persist after its predecessor
  // failed, because that would violate the transcript's FIFO ordering contract.
  private rejectQueuedTranscriptPersists(
    error: unknown,
    failureContext: Pick<
      VoiceTurnPersistFailure,
      | "rootProviderEventId"
      | "attemptCount"
      | "affectedTurnCount"
      | "openaiCallId"
    >
  ) {
    const message = errorMessage(error, "Could not save transcript.")
    while (this.pendingTranscriptPersists.length > 0) {
      const pending = this.pendingTranscriptPersists[0]
      this.pendingTranscriptPersists.shift()
      this.deps.onTurnPersistFailed({
        providerEventId: pending.turn.providerEventId,
        message,
        ...failureContext,
      })
      voiceTracer(
        "persist",
        {
          providerEventId: pending.turn.providerEventId,
          role: pending.turn.role,
        },
        pending.eventSeq
      ).warn("transcript_persist_failed", {
        interrupted: pending.turn.interrupted === true,
        transcriptLength: pending.turn.transcript.length,
        message,
      })
      pending.deferred.reject(error)
    }
  }

  private startTranscriptPersistWorker() {
    if (this.persistWorkerRunning) return
    this.persistWorkerSettled = this.runTranscriptPersistWorker()
  }
}
